import React from 'react';
import Box from '@amzn/meridian/box';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import SearchField from '@amzn/meridian/search-field';
import { AssignmentAssociate } from '../models';
import AssociateCard from './AssociateCard';
import './AssociateSidebar.scss';

export interface AssociateSidebarProps {
  /** The unassigned associate pool, already filtered by the parent. */
  unassignedAssociates: AssignmentAssociate[];
  /** Current search text (owned by the parent page). */
  searchQuery: string;
  /** Fires as the user types or submits the search field. */
  onSearchChange: (value: string) => void;
  /**
   * Heading shown above the count badge. Defaults to `"Unassigned"`. Pass a
   * different label (e.g. `"PA / Lead / HR"`) to reuse this sidebar for a
   * second associate pool rendered below the unassigned box.
   */
  title?: string;
  /**
   * Optional per-associate assign action, forwarded to each AssociateCard.
   * When omitted, cards render without an "Assign" button (the assignment
   * flow lands in a later slice).
   */
  onAssignAssociate?: (associateId: string) => void;
  /**
   * Optional per-associate "view decision" action, forwarded to each
   * AssociateCard as its info (ℹ️) button. Used by the PA/Lead/HR box so a
   * planner can inspect why the associate was excluded (PA_LEAD_HR_EXCLUDED).
   */
  onViewDecision?: (associateId: string) => void;
}

/**
 * Left-column sidebar for the redesigned Station Assignment screen. Lists the
 * currently unassigned associates with a live count badge and a
 * case-insensitive name search.
 *
 * Presentational: the search text and the (already-filtered) unassigned list
 * are owned by the parent page, so this component holds no state of its own.
 * Built from Meridian primitives (Box/Row/Column/Text/SearchField) plus the
 * AssociateCard child.
 */
const AssociateSidebar: React.FC<AssociateSidebarProps> = ({
  unassignedAssociates,
  searchQuery,
  onSearchChange,
  title = 'Unassigned',
  onAssignAssociate,
  onViewDecision,
}) => (
  <Box
    className="assignments-associate-sidebar"
    type="outline"
    spacingInset="300"
    data-testid="associate-sidebar"
  >
    <Row alignmentVertical="center" spacing="200">
      <Text type="h100">{title}</Text>
      <Box
        className="assignments-associate-sidebar__count-badge"
        data-testid="associate-sidebar-count"
      >
        <Text type="b100" color="inverted">
          {unassignedAssociates.length}
        </Text>
      </Box>
    </Row>

    <Box className="assignments-associate-sidebar__search">
      <SearchField
        value={searchQuery}
        onChange={onSearchChange}
        onSubmit={onSearchChange}
        placeholder="Search associates"
        data-testid="associate-sidebar-search"
      />
    </Box>

    <Column className="assignments-associate-sidebar__list" spacing="100">
      {unassignedAssociates.map((associate) => (
        <AssociateCard
          key={associate.associateId}
          associate={associate}
          onAssign={onAssignAssociate}
          onViewDecision={onViewDecision}
        />
      ))}
    </Column>
  </Box>
);

export default AssociateSidebar;

import './AssociateSidebar.scss';